import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dropzone from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faXmark } from "@fortawesome/free-solid-svg-icons";

import api from "../../api/http";
import "../../styles/profile/Profiles.css";

/* =========================
   Phone helpers (RELAXED)
========================= */
const normalizeMdPhone = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";

  const hasPlus = s.startsWith("+");
  const digits = s.replace(/[^\d]/g, "");

  // deja international
  if (hasPlus) return `+${digits}`;

  // MD: 0XXXXXXXX -> +373XXXXXXXX
  if (digits.length === 9 && digits.startsWith("0")) {
    return `+373${digits.slice(1)}`;
  }

  // MD: 8 cifre -> +373XXXXXXXX
  if (digits.length === 8) {
    return `+373${digits}`;
  }

  // fallback (backend decide)
  return digits;
};

const looksValidEnough = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return true; // gol = OK
  const digits = s.replace(/[^\d]/g, "");
  return digits.length >= 8; // relaxat
};

const Profile = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access");

  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [error, setError] = useState("");

  const [userProfile, setUserProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    birthday: "",
    gender: 2,
    avatar: "",
  });

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    birthday: "",
    gender: 2,
  });

  const ensureAuth = () => {
    if (!token) {
      navigate("/login", { replace: true });
      return false;
    }
    return true;
  };

  const loadProfile = async () => {
    if (!ensureAuth()) return;

    setLoading(true);
    setError("");

    try {
      const res = await api.get("/users/profiles/me/");
      const p = res.data;

      setUserProfile({
        first_name: p.first_name ?? "",
        last_name: p.last_name ?? "",
        email: p?.user?.email ?? p.email ?? "",
        phone_number: p.phone_number ?? "",
        birthday: p.birthday ?? "",
        gender: typeof p.gender === "number" ? p.gender : 2,
        avatar: p.avatar ?? "",
      });
    } catch (e) {
      console.error("Profile load failed", e);
      setError("Nu am putut încărca profilul.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenEditForm = () => {
    setFormData({
      first_name: userProfile.first_name,
      last_name: userProfile.last_name,
      phone_number: userProfile.phone_number,
      birthday: userProfile.birthday,
      gender: userProfile.gender ?? 2,
    });
    setAvatarFile(null);
    setError("");
    setShowEditForm(true);
  };

  const handleCloseEditForm = () => {
    setShowEditForm(false);
    setAvatarFile(null);
    setError("");
  };

  const handleChangePassword = () => navigate("/change-password");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: name === "gender" ? Number(value) : value,
    }));
  };

  const handleImageUpload = (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (file) setAvatarFile(file);
  };

  const handleUpdateProfile = async () => {
    if (!ensureAuth()) return;

    setError("");

    try {
      const fd = new FormData();

      // trimitem câmpurile normale
      Object.entries(formData).forEach(([k, v]) => {
        if (k === "phone_number") return; // separat
        if (v !== "" && v !== null && typeof v !== "undefined") {
          fd.append(k, v);
        }
      });

      // phone_number — RELAXED
      const rawPhone = formData.phone_number;
      if (rawPhone && String(rawPhone).trim() !== "") {
        if (looksValidEnough(rawPhone)) {
          const normalized = normalizeMdPhone(rawPhone);
          fd.append("phone_number", normalized);
        } else {
          // nu blocăm update-ul
          setError(
            "Numărul de telefon pare prea scurt. Profilul se salvează fără telefon."
          );
        }
      }

      if (avatarFile) fd.append("avatar", avatarFile);

      await api.patch("/users/profiles/me/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await loadProfile();
      setShowEditForm(false);
    } catch (e) {
      console.error("Profile update failed", e);

      const data = e?.response?.data;
      if (data && typeof data === "object") {
        const msg = Object.entries(data)
          .map(([k, v]) =>
            `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`
          )
          .join("\n");
        setError(msg || "Eroare la actualizare profil.");
      } else {
        setError("Eroare la actualizare profil.");
      }
    }
  };

  const genderLabel =
    userProfile.gender === 0
      ? "Male"
      : userProfile.gender === 1
      ? "Female"
      : "Unspecified";

  return (
      <div className="srProf_page">
    <div className="srProf_wrap">
      <div className="srProf_layout">
        {/* TOP: Settings bar — icon + resetare parolă */}
        <aside className="srProf_settings">
          <div className="srProf_settingsLabel">
            <FontAwesomeIcon icon={faCog} className="srProf_settingsIcon" aria-hidden />
            <span>Settings</span>
          </div>
          <button
            type="button"
            className="srProf_btn srProf_btnPrimary srProf_btnPassword"
            onClick={handleChangePassword}
          >
            Schimbă parola
          </button>
        </aside>

        {/* Personal Information */}
        <section className="srProf_card">
          <div className="srProf_cardHeader">
            <h2 className="srProf_title">Personal Information</h2>

            {!loading && (
              <button
                className="srProf_btn srProf_btnSoft"
                onClick={handleOpenEditForm}
              >
                Edit
              </button>
            )}
          </div>

          {loading ? (
            <p className="srProf_muted">Loading...</p>
          ) : (
            <div className="srProf_grid">
              <div className="srProf_info">
                <div className="srProf_row">
                  <span className="srProf_k">Name</span>
                  <span className="srProf_v">
                    {userProfile.first_name} {userProfile.last_name}
                  </span>
                </div>

                <div className="srProf_row">
                  <span className="srProf_k">Email</span>
                  <span className="srProf_v">{userProfile.email}</span>
                </div>

                <div className="srProf_row">
                  <span className="srProf_k">Phone</span>
                  <span className="srProf_v">
                    {userProfile.phone_number || "-"}
                  </span>
                </div>

                <div className="srProf_row">
                  <span className="srProf_k">Date of Birth</span>
                  <span className="srProf_v">
                    {userProfile.birthday || "-"}
                  </span>
                </div>

                <div className="srProf_row">
                  <span className="srProf_k">Gender</span>
                  <span className="srProf_v">{genderLabel}</span>
                </div>

                {error && <pre className="srProf_error">{error}</pre>}
              </div>

              <div className="srProf_avatarCol">
                <div className="srProf_avatarBox">
                  {userProfile.avatar ? (
                    <img
                      src={userProfile.avatar}
                      alt="Avatar"
                      className="srProf_avatarImg"
                    />
                  ) : (
                    <div className="srProf_avatarPlaceholder">No Avatar</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* MODAL */}
      {showEditForm && (
        <div className="srProfModal_overlay" role="dialog" aria-modal="true">
          <div className="srProfModal_content">
            <div className="srProfModal_header">
              <h3 className="srProfModal_heading">Edit Profile</h3>

              <button
                className="srProfModal_closeBtn"
                onClick={handleCloseEditForm}
                type="button"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="srProfModal_body">
              <form
                className="srProfF_form"
                onSubmit={(e) => e.preventDefault()}
              >
                <label>
                  First Name
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                </label>

                <label>
                  Last Name
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </label>

                <label>
                  Phone Number
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="ex: 060597932 sau +37360597932"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </label>

                <label>
                  Birthday
                  <input
                    type="date"
                    name="birthday"
                    value={formData.birthday}
                    onChange={handleChange}
                  />
                </label>

                <label className="srProfF_full">
                  Gender
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                  >
                    <option value={2}>Not specified</option>
                    <option value={0}>Male</option>
                    <option value={1}>Female</option>
                  </select>
                </label>

                <div className="srProfF_full">
                  <Dropzone
                    onDrop={handleImageUpload}
                    multiple={false}
                    accept={{ "image/*": [] }}
                  >
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <div
                        className={`srProf_dropzone ${
                          isDragActive ? "is-drag" : ""
                        }`}
                        {...getRootProps()}
                      >
                        <input {...getInputProps()} />
                        {avatarFile ? (
                          <p className="srProf_dropHint">
                            Selected: {avatarFile.name}
                          </p>
                        ) : (
                          <p className="srProf_dropHint">
                            Click here to upload a new avatar
                          </p>
                        )}
                      </div>
                    )}
                  </Dropzone>
                </div>

                {error && (
                  <pre className="srProf_error srProfF_full">{error}</pre>
                )}

                <div className="srProfF_actions">
                  <button
                    type="button"
                    className="srProf_btn srProf_btnPrimary"
                    onClick={handleUpdateProfile}
                  >
                    Update Profile
                  </button>

                  <button
                    type="button"
                    className="srProf_btn srProf_btnDanger"
                    onClick={handleCloseEditForm}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
          </div>
  );
};

export default Profile;